import { Injectable } from "@angular/core";
import { take } from "rxjs";
import { IRestLayerRequest } from "@gtmhub/core";
import { IGoal } from "@gtmhub/goals/models";
import { GoalsChangedEventArgs, OKRsEventType } from "@gtmhub/okrs/events";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { ReactionsFacade } from "@webapp/comments/services/reactions-facade.service";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { GoalsFacade } from "@webapp/okrs/goals/services/goals-facade.service";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { MetricsFacade } from "@webapp/okrs/metrics/services/metrics-facade.service";
import { metricProperties } from "@webapp/okrs/utils/utils";
import { PluginStateService } from "@webapp/plugins/components/plugin/services/plugin-state.service";
import { PluginService } from "@webapp/plugins/components/plugin/services/plugin.service";
import {
  DeleteRequestMetricSnapshot,
  DeleteRequestReaction,
  GetRequest,
  PatchRequestMetricComment,
  PluginPayload,
  PostRequestReaction,
} from "@webapp/plugins/plugins.models";
import { PluginsFacade } from "@webapp/plugins/services/plugins-facade.service";
import { mergeCustomFields, setParams } from "@webapp/plugins/utils";

@Injectable()
export class PluginOkrsService extends PluginService {
  private readonly sessionIdMap;

  constructor(
    private goalsFacade: GoalsFacade,
    private broadcastService: BroadcastService,
    private metricsFacade: MetricsFacade,
    private pluginsFacade: PluginsFacade,
    private reactionsFacade: ReactionsFacade,
    pluginStateService: PluginStateService,
    destroy$: DestroyService
  ) {
    super(pluginStateService, destroy$);
    this.sessionIdMap = reduxStoreContainer.reduxStore.getState<ISessionsStoreState>().sessions.map;
    this.broadcastService.on<GoalsChangedEventArgs>(OKRsEventType.OKRS_GOALS_CHANGED).subscribe((args): void => this.onGoalsChanged(args));
  }

  public getCurrentItem(type: string): void {
    this.postMessage(this.item, type);
  }

  public updateCurrentItem(type: string, item: (IGoal | Metric) & { pluginSettings: { pluginId: string } }): void {
    const pluginId = item.pluginSettings.pluginId;

    if (this.context === "goal") {
      this.goalsFacade
        .patchGoal$(item.id, item as IGoal, setParams(pluginId))
        .pipe(take(1))
        .subscribe((updatedItem) => {
          this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, { reason: "goalChanged" });
          this.broadcastService.emit("goalsChanged");
          this.postMessage(updatedItem, type);
        });
    }

    if (this.context === "metric") {
      this.metricsFacade
        .patchMetric$(item as Metric, setParams(pluginId))
        .pipe(take(1))
        .subscribe((updatedMetric) => {
          this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, { reason: "goalChanged" });
          this.broadcastService.emit("goalsChanged");
          this.postMessage(updatedMetric, type);
        });
    }
  }

  public getSession(type: string, data: GetRequest): void {
    this.postMessage(this.sessionIdMap[data.id], type);
  }

  public getMetric(type: string, data: GetRequest): void {
    this.metricsFacade
      .getMetric$(data.id, metricProperties, setParams(data.pluginSettings.pluginId))
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public getMetrics(type: string, data: PluginPayload<IRestLayerRequest>): void {
    this.metricsFacade
      .getMetricsV2$(
        {
          filter: data.payload.filter,
          fields: data.payload.fields.split(","),
          limit: data.payload.limit,
          page: data.payload.page,
          skip: data.payload.skip,
          sort: data.payload.sort.split(","),
        },
        setParams(data.pluginSettings.pluginId)
      )
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public createMetric(type: string, data: PluginPayload<Metric>): void {
    this.metricsFacade
      .createMetric$(data.payload, setParams(data.pluginSettings.pluginId))
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public updateMetric(type: string, data: PluginPayload<Metric>): void {
    this.metricsFacade
      .getMetric$(data.payload.id)
      .pipe(take(1))
      .subscribe((metric) => {
        const existingCustomFields = metric.customFields;
        const newCustomFields = data.payload.customFields;

        /** ensure to merge both objects, otherwise it will omit the customfields not specified in payload */
        if (newCustomFields) {
          data.payload.customFields = mergeCustomFields(existingCustomFields, newCustomFields);
        }

        this.metricsFacade
          .patchMetric$(data.payload, setParams(data.pluginSettings.pluginId))
          .pipe(take(1))
          .subscribe({
            next: (response) => {
              this.postMessage(response, type);
            },
            error: (error) => {
              this.postMessage(this.pluginsFacade.transformHttpError(error), type);
            },
          });
      });
  }

  public patchMetricComment(type: string, data: PluginPayload<PatchRequestMetricComment>): void {
    this.metricsFacade
      .patchMetricComment$(data.payload.metricHistoryId, data.payload.comment)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public createMetricReaction(type: string, data: PluginPayload<PostRequestReaction>): void {
    this.reactionsFacade
      .createReaction$(data.payload.createReaction, { isReactionOnGif: data.payload.isReactionOnGif })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public deleteMetricReaction(type: string, data: PluginPayload<DeleteRequestReaction>): void {
    this.reactionsFacade
      .deleteReaction$(data.payload.reactionName, data.payload.targetId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public deleteMetric(type: string, data: GetRequest): void {
    this.metricsFacade
      .getMetric$(data.id, metricProperties, setParams(data.pluginSettings.pluginId))
      .pipe(take(1))
      .subscribe((metric) => {
        this.metricsFacade
          .deleteMetric$(metric.goalId, data.id, setParams(data.pluginSettings.pluginId))
          .pipe(take(1))
          .subscribe({
            next: (response) => {
              this.postMessage(response, type);
            },
            error: (error) => {
              this.postMessage(this.pluginsFacade.transformHttpError(error), type);
            },
          });
      });
  }

  public deleteMetricSnapshot(type: string, data: PluginPayload<DeleteRequestMetricSnapshot>): void {
    this.metricsFacade
      .getMetric$(data.payload.metricId, metricProperties, setParams(data.pluginSettings.pluginId))
      .pipe(take(1))
      .subscribe((metric) => {
        this.metricsFacade
          .deleteSnapshot$(metric.goalId, data.payload.metricId, data.payload.snapshotId)
          .pipe(take(1))
          .subscribe({
            next: (response) => {
              this.postMessage(response, type);
            },
            error: (error) => {
              this.postMessage(this.pluginsFacade.transformHttpError(error), type);
            },
          });
      });
  }

  public checkinMetric(type: string, data: PluginPayload<Metric, { checkin }>): void {
    this.metricsFacade
      .checkin$({ id: data.payload.id, goalId: data.payload.goalId } as Metric, data.payload.checkin, { pluginId: data.pluginSettings.pluginId })
      .pipe(take(1))
      .subscribe((newCheckin) => {
        this.goalsFacade
          .getGoal$(data.payload.goalId)
          .pipe(take(1))
          .subscribe((goal) => {
            this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, { reason: "goalChanged" });
            this.broadcastService.emit("goalsChanged");

            this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, {
              reason: "metricSnapshotCreated",
              detail: {
                checkin: newCheckin,
                metric: data.payload,
                goal,
                session: this.sessionIdMap[data.payload.sessionId],
              },
            });

            this.postMessage(newCheckin, type);
          });
      });
  }

  public getGoal(type: string, data: GetRequest): void {
    this.goalsFacade
      .getGoal$(data.id, null, setParams(data.pluginSettings.pluginId))
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public getGoals(type: string, data: PluginPayload<IRestLayerRequest>): void {
    this.goalsFacade
      .getGoalsV2$(
        {
          filter: data.payload.filter,
        },
        setParams(data.pluginSettings.pluginId)
      )
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public createGoal(type: string, data: PluginPayload<IGoal>): void {
    this.goalsFacade
      .createGoal$(data.payload, null, setParams(data.pluginSettings.pluginId))
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public updateGoal(type: string, data: PluginPayload<IGoal>): void {
    this.goalsFacade
      .getGoal$(data.payload.id)
      .pipe(take(1))
      .subscribe((goal) => {
        const existingCustomFields = goal.customFields;
        const newCustomFields = data.payload.customFields;

        /** ensure to merge both objects, otherwise it will omit the customfields not specified in palyoad */
        if (newCustomFields) {
          data.payload.customFields = mergeCustomFields(existingCustomFields, newCustomFields);
        }

        this.goalsFacade
          .patchGoal$(data.payload.id, data.payload, setParams(data.pluginSettings.pluginId))
          .pipe(take(1))
          .subscribe({
            next: (response) => {
              this.postMessage(response, type);
            },
            error: (error) => {
              this.postMessage(this.pluginsFacade.transformHttpError(error), type);
            },
          });
      });
  }

  public deleteGoal(type: string, data: GetRequest): void {
    this.goalsFacade
      .deleteGoal$(data.id, setParams(data.pluginSettings.pluginId))
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public onGoalsChanged(data: GoalsChangedEventArgs): void {
    if (data.reason.startsWith("metric")) {
      this.onMetricUpdated(data);
    } else {
      this.onGoalUpdated(data);
    }
  }

  public onMetricUpdated(data: GoalsChangedEventArgs): void {
    this.postMessage(data, "onMetricUpdated");
  }

  public onGoalUpdated(data: GoalsChangedEventArgs): void {
    this.postMessage(data, "onGoalUpdated");
  }
}
